import { Plugins } from "@capacitor/core";

const { Storage } = Plugins;

const setObjStorage = async (key: string, value: string, encrypt: boolean) => {
  const storeValue = encrypt ? btoa(value) : value;
  await Storage.set({
    key: key,
    value: JSON.stringify(storeValue),
  });
};

const getObjStorage = async (key: string, encrypt: boolean) => {
  const value: any = await Storage.get({ key: key });
  const storeValue = encrypt ? atob(value) : value;
  return JSON.parse(storeValue);
};

const setItem = async (
  key: string,
  value: string,
  encrypt: boolean = false
) => {
  const storeValue = encrypt ? btoa(value) : value;
  await Storage.set({
    key: key,
    value: storeValue,
  });
};

const getItem = async (key: string, encrypt: boolean = false) => {
  const value: any = await Storage.get({ key: key });
  const storeValue = encrypt ? atob(value.value) : value;
  return storeValue;
};

/**
 * NOTE ON THE `encrypt` FLAG
 *
 * It is base64, not encryption -- anything stored this way is readable by
 * anyone who can read the storage. It is kept because existing installs have
 * values encoded this way, but it protects nothing; treat storage as plain
 * text when deciding what may go in it.
 */

/**
 * base64 that survives non-Latin-1 text.
 *
 * Plain btoa() throws "Invalid character" on any character above U+00FF, so an
 * Arabic name -- expected in this deployment -- threw while the login handler
 * was writing the session to storage. The throw escaped onLoginSubmit, which
 * has no catch, so the token was never stored, the redirect never ran, and the
 * spinner span forever after a *successful* sign-in.
 */
const encodeValue = (value: string): string =>
  btoa(unescape(encodeURIComponent(value)));

/**
 * The inverse. Values written before this fix were raw Latin-1, which is not
 * valid UTF-8, so a failed decode falls back to the bytes as they are rather
 * than throwing on data this app itself wrote.
 */
const decodeValue = (value: string): string => {
  const binary = atob(value);
  try {
    return decodeURIComponent(escape(binary));
  } catch {
    return binary;
  }
};

const setStorageItem = (storageType: any, data: any, encrypt?: boolean) => {
  // tslint:disable-next-line:forin
  for (const key in data) {
    const value = encrypt ? encodeValue(String(data[key])) : data[key];
    storageType.setItem(key, value);
  }
};

const getStorageItem = (storageType: any, key: string, encrypt?: boolean) => {
  const data = storageType.getItem(key);

  // A missing key used to be handed straight to atob(), and atob(null)
  // returns the garbage string "ée" instead of throwing -- so callers that
  // checked for null saw a value that was not there.
  if (!encrypt || data === null || data === undefined) {
    return data;
  }

  try {
    return decodeValue(data);
  } catch (error) {
    console.error(`Stored value for "${key}" could not be read`, error);
    return null;
  }
};

const removeStorageItem = (storageType: any, keys: any) => {
  for (const key of keys) {
    storageType.removeItem(key);
  }
};

export const StorageService = {
  setItem,
  getItem,
  setObjStorage,
  getObjStorage,
  setStorageItem,
  getStorageItem,
  removeStorageItem,
};
